import React, {
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useState
} from "react";

export interface Alert {
  id: number | string;
  message: ReactNode;
  severity: "success" | "info" | "warning" | "error";
}

interface AlertBarContextProps {
  addAlert: (alert: Alert) => void;
  alerts: Alert[];
  removeAlert: (id: number | string) => void;
}

export const AlertBarContext = createContext<AlertBarContextProps | undefined>(
  undefined
);

interface AlertBarProviderProps {
  children: ReactNode;
}

export const AlertBarProvider: React.FC<AlertBarProviderProps> = ({
  children
}) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const addAlert: AlertBarContextProps["addAlert"] = useCallback((alert) => {
    setAlerts((prev) => [...prev, alert]);
  }, []);

  const removeAlert: AlertBarContextProps["removeAlert"] = useCallback(
    (id: number | string) => {
      setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id));
    },
    []
  );

  const value = useMemo(
    () => ({ addAlert, alerts, removeAlert }),
    [addAlert, alerts, removeAlert]
  );

  return (
    <AlertBarContext.Provider value={value}>
      {children}
    </AlertBarContext.Provider>
  );
};

export default function useAlertBar() {
  const alertBarContext = React.useContext(AlertBarContext);
  if (!alertBarContext) {
    throw new Error(`useAlertBar must be used within a AlertBarProvider`);
  }
  const { addAlert, removeAlert, alerts } = alertBarContext;
  return { addAlert, removeAlert, alerts };
}
